import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect } from "react";
import PharmatechLayout from "../components/PharmatechLayout";
import { AlertTriangle, Plus, Send, Loader2, CheckCircle2, BarChart3, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const SEVERITY_OPTIONS = [
  { value: "legere", label: "Legere", color: "#22c55e", bg: "bg-green-50" },
  { value: "moderee", label: "Moderee", color: "#f59e0b", bg: "bg-amber-50" },
  { value: "grave", label: "Grave", color: "#ef4444", bg: "bg-red-50" },
  { value: "critique", label: "Critique", color: "#7c3aed", bg: "bg-purple-50" },
];

export default function PharmacovigilancePage() {
  const [tab, setTab] = useState("report");
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ drug_name: "", effect_description: "", severity: "moderee", patient_age: "", patient_sexe: "", onset_delay: "", outcome: "", notes: "" });

  useEffect(() => { if (tab === "list") loadReports(); if (tab === "stats") loadStats(); }, [tab]);

  const loadReports = async () => { try { const r = await axios.get(`${API}/pharmatech/pharmacovigilance/reports`, { headers: getHeaders() }); setReports(r.data.reports || []); } catch {} };
  const loadStats = async () => { try { const r = await axios.get(`${API}/pharmatech/pharmacovigilance/stats`, { headers: getHeaders() }); setStats(r.data); } catch {} };

  const submitReport = async () => {
    if (!form.drug_name.trim() || !form.effect_description.trim()) { toast.error("Medicament et description requis"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/pharmatech/pharmacovigilance/report`, {
        ...form,
        patient_age: form.patient_age ? parseInt(form.patient_age) : null,
      }, { headers: getHeaders() });
      toast.success("Signalement enregistre avec succes");
      setForm({ drug_name: "", effect_description: "", severity: "moderee", patient_age: "", patient_sexe: "", onset_delay: "", outcome: "", notes: "" });
    } catch (e) { toast.error("Erreur: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };

  return (
    <PharmatechLayout title="Pharmacovigilance" subtitle="Signalement et suivi des effets indesirables">
      <div data-testid="pharmacovigilance-page" className="max-w-4xl mx-auto space-y-6">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          {[{ key: "report", label: "Nouveau signalement", icon: Plus }, { key: "list", label: "Signalements", icon: AlertTriangle }, { key: "stats", label: "Statistiques", icon: BarChart3 }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${tab === t.key ? "bg-blue-50 text-blue-700 border border-blue-200" : "text-slate-500 hover:bg-slate-50"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Report form */}
        {tab === "report" && (
          <div className="space-y-4">
            <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
              <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" /> Signaler un effet indesirable
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase mb-1 block">Medicament concerne *</label>
                  <input data-testid="vigilance-drug" value={form.drug_name} onChange={e => setForm(f => ({...f, drug_name: e.target.value}))}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="Nom du médicament (DCI ou commercial)" />
                </div>

                <div>
                  <label className="text-[10px] text-slate-500 uppercase mb-1 block">Description de l'effet indesirable *</label>
                  <textarea data-testid="vigilance-effect" value={form.effect_description} onChange={e => setForm(f => ({...f, effect_description: e.target.value}))}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-400 h-24 resize-none" placeholder="Decrivez les symptomes observes..." />
                </div>

                <div>
                  <label className="text-[10px] text-slate-500 uppercase mb-2 block">Gravite</label>
                  <div className="flex gap-2">
                    {SEVERITY_OPTIONS.map(s => (
                      <button key={s.value} onClick={() => setForm(f => ({...f, severity: s.value}))} data-testid={`severity-${s.value}`}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.severity === s.value ? `${s.bg} border-current` : "bg-white border-slate-200 text-slate-500"}`}
                        style={form.severity === s.value ? { color: s.color, borderColor: s.color } : {}}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[10px] text-slate-500 uppercase mb-1 block">Age patient</label>
                    <input type="number" value={form.patient_age} onChange={e => setForm(f => ({...f, patient_age: e.target.value}))}
                      className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 bg-slate-50" placeholder="Ex: 45" /></div>
                  <div><label className="text-[10px] text-slate-500 uppercase mb-1 block">Sexe</label>
                    <select value={form.patient_sexe} onChange={e => setForm(f => ({...f, patient_sexe: e.target.value}))}
                      className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 bg-slate-50"><option value="">--</option><option value="M">M</option><option value="F">F</option></select></div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[10px] text-slate-500 uppercase mb-1 block">Delai d'apparition</label>
                    <input value={form.onset_delay} onChange={e => setForm(f => ({...f, onset_delay: e.target.value}))}
                      className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 bg-slate-50" placeholder="Ex: 2 jours apres debut" /></div>
                  <div><label className="text-[10px] text-slate-500 uppercase mb-1 block">Issue/Evolution</label>
                    <input value={form.outcome} onChange={e => setForm(f => ({...f, outcome: e.target.value}))}
                      className="w-full px-3 py-2 rounded-lg text-sm border border-slate-200 bg-slate-50" placeholder="Ex: Resolution apres arret" /></div>
                </div>

                <div><label className="text-[10px] text-slate-500 uppercase mb-1 block">Notes supplementaires</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                    className="w-full px-3 py-2.5 rounded-lg text-sm border border-slate-200 bg-slate-50 h-16 resize-none" placeholder="Autres informations pertinentes..." /></div>
              </div>
            </div>

            <Button onClick={submitReport} disabled={loading} data-testid="submit-vigilance-btn" className="w-full py-3 text-sm font-bold rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Envoi...</> : <><Send className="w-4 h-4 mr-2" /> Envoyer le signalement</>}
            </Button>

            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-[10px] text-amber-700"><Shield className="w-3 h-3 inline mr-1" />Les signalements sont transmis de manière anonyme et contribuent à la sécurité collective des patients.</p>
            </div>
          </div>
        )}

        {/* Reports list */}
        {tab === "list" && (
          <div className="space-y-3">
            {reports.map((r, i) => {
              const sev = SEVERITY_OPTIONS.find(s => s.value === r.severity) || SEVERITY_OPTIONS[1];
              return (
                <div key={i} className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm" data-testid={`report-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${sev.bg}`} style={{ color: sev.color }}>{sev.label}</span>
                    <span className="text-xs font-bold text-slate-800">{r.drug_name}</span>
                    <span className="text-[10px] text-slate-400 ml-auto">{new Date(r.timestamp).toLocaleDateString("fr-FR")}</span>
                  </div>
                  <p className="text-xs text-slate-600">{r.effect_description}</p>
                  <div className="flex gap-3 mt-2 text-[10px] text-slate-400">
                    {r.patient_age && <span>Age: {r.patient_age}</span>}
                    {r.patient_sexe && <span>Sexe: {r.patient_sexe}</span>}
                    {r.onset_delay && <span>Delai: {r.onset_delay}</span>}
                    {r.outcome && <span>Issue: {r.outcome}</span>}
                  </div>
                </div>
              );
            })}
            {reports.length === 0 && <p className="text-center text-sm text-slate-400 py-12">Aucun signalement enregistre</p>}
          </div>
        )}

        {/* Stats */}
        {tab === "stats" && stats && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm text-center">
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                <p className="text-xs text-slate-500">Total signalements</p>
              </div>
              {SEVERITY_OPTIONS.map(s => (
                <div key={s.value} className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm text-center">
                  <p className="text-2xl font-bold" style={{ color: s.color }}>{stats.by_severity[s.value] || 0}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              ))}
            </div>

            {stats.top_drugs.length > 0 && (
              <div className="p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Medicaments les plus signales</h3>
                <div className="space-y-2">
                  {stats.top_drugs.map((d, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs font-bold text-slate-400 w-6">{i + 1}.</span>
                      <span className="text-xs font-medium text-slate-700 flex-1">{d.drug}</span>
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-600">{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PharmatechLayout>
  );
}
